import type {TaillightsSyntaxColors} from '@github-ui/taillights-worker'
import type {PreparedText} from '@github-ui/taillights-worker/document'
import type {TaillightTextBinding} from './taillight-dom'

let nextHighlight = 0

export function createTaillightsCSSScope() {
  const states = new Map<
    Document,
    {
      sheet: CSSStyleSheet
      buckets: Map<string, {name: string; highlight: Highlight; rule: CSSStyleRule}>
    }
  >()
  function register(binding: TaillightTextBinding, source: PreparedText, colors: TaillightsSyntaxColors) {
    if (typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') {
      throw new Error('The CSS Highlight API is unavailable in this browser.')
    }
    const document = binding.element.ownerDocument
    if (typeof CSSStyleSheet !== 'function' || !Array.isArray(document.adoptedStyleSheets)) {
      throw new Error('Taillight CSS rendering requires constructed and adopted stylesheets.')
    }
    let state = states.get(document)
    if (!state) {
      const sheet = new CSSStyleSheet()
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet]
      state = {sheet, buckets: new Map()}
      states.set(document, state)
    }
    const owned: Array<{range: Range; highlight: Highlight}> = []
    const registeredState = state
    const release = () => {
      for (const {range, highlight} of owned) highlight.delete(range)
      for (const [color, bucket] of registeredState.buckets) {
        if (bucket.highlight.size) continue
        if (CSS.highlights.get(bucket.name) === bucket.highlight) CSS.highlights.delete(bucket.name)
        const index = Array.from(registeredState.sheet.cssRules).indexOf(bucket.rule)
        if (index >= 0) registeredState.sheet.deleteRule(index)
        registeredState.buckets.delete(color)
      }
      if (!registeredState.buckets.size) {
        document.adoptedStyleSheets = document.adoptedStyleSheets.filter(sheet => sheet !== registeredState.sheet)
        if (states.get(document) === registeredState) states.delete(document)
      }
    }
    try {
      for (const run of source.styleRuns) {
        const color = run.colorKey && Object.hasOwn(colors, run.colorKey) ? colors[run.colorKey] : undefined
        if (!color || run.end <= run.start) continue
        if (!CSS.supports('color', color)) throw new Error(`Unsupported Taillight foreground: ${color}`)
        let bucket = state.buckets.get(color)
        if (!bucket) {
          const name = `taillight-${++nextHighlight}`
          const highlight = new Highlight()
          const index = state.sheet.insertRule(`::highlight(${name}) {}`, state.sheet.cssRules.length)
          const rule = state.sheet.cssRules[index]
          if (!(rule instanceof CSSStyleRule)) throw new Error('Unable to register Taillight highlight styles.')
          rule.style.setProperty('color', color)
          CSS.highlights.set(name, highlight)
          bucket = {name, highlight, rule}
          state.buckets.set(color, bucket)
        }
        const range = binding.range(run.start, run.end)
        bucket.highlight.add(range)
        owned.push({range, highlight: bucket.highlight})
      }
    } catch (error) {
      release()
      throw error
    }
    return release
  }
  return {
    register,
    dispose() {
      for (const [document, state] of states) {
        for (const {name, highlight} of state.buckets.values()) {
          if (CSS.highlights.get(name) === highlight) CSS.highlights.delete(name)
        }
        document.adoptedStyleSheets = document.adoptedStyleSheets.filter(sheet => sheet !== state.sheet)
      }
      states.clear()
    },
  }
}
